import { Viewer, Cesium3DTile, Cesium3DTileset } from "cesium";
import { AttachablePluginInterface } from "components/elements/CesiumViewer/core/wrapper/CesiumWrapper";
import TilesetPlugin, { TilesetFeature } from "./TilesetPlugin";
import TilesetStylingPlugin, {
  TilesetStylePriority,
} from "components/elements/CesiumViewer/core/wrapper/TilesetStylingPlugin";
import { ObservableProperty } from "./ObservableProperty";
import { Guard } from "lib/actions/util";

type SelectedElement = {
  tilesetId: string;
  elementId: string;
};

type FeatureProperty = {
  name: string;
  value: string;
};

// Cesium declares getFeature with the feature id alone, but vector content
// also takes the property table id and can return nothing.
type TileContentFeatures = {
  featuresLength: number;
  getFeature(
    featureId: number,
    propertyTableId: number,
  ): TilesetFeature | undefined;
};

export default class SelectedElementPlugin
  implements AttachablePluginInterface
{
  private _viewer: Viewer | null = null;
  private _selectedElement: SelectedElement | null = null;
  private _selectedFeature: TilesetFeature | null = null;
  private _selectedProperties: FeatureProperty[] | null = null;
  private _detachTileVisibleHandler?: () => void;
  private readonly _isFeatureSelected = new ObservableProperty<boolean>(false);

  get isFeatureSelected() {
    return this._isFeatureSelected;
  }

  get selectedProperties(): FeatureProperty[] | null {
    return this._selectedProperties;
  }

  constructor(
    private readonly _tilesetPlugin: TilesetPlugin,
    private readonly _tilesetStylingPlugin: TilesetStylingPlugin,
  ) {}

  setViewer(viewer: Viewer): void {
    this._viewer = viewer;
    this._detachTileVisibleHandler = this._tilesetPlugin.tileVisible.addHandler(
      this.handleTileVisible.bind(this),
    );
  }

  detachViewer(): void {
    this.clearSelection();
    this._detachTileVisibleHandler?.();
    this._detachTileVisibleHandler = undefined;
    this._viewer = null;
  }

  async setSelectedElement(tilesetId: string | null, elementId: string | null) {
    if (!tilesetId || !elementId) {
      await this.clearSelection();
      return;
    }

    await this.removeStylingForCurrentSelection();
    this._selectedElement = { tilesetId, elementId };
    this._selectedFeature = null;
    this._selectedProperties = null;
    this._isFeatureSelected.set(false);
    await this.applyStylingForCurrentSelection();
  }

  async setSelectedFeature(feature: TilesetFeature | null) {
    if (!feature) {
      await this.clearSelection();
      return;
    }

    Guard.require(feature, "feature");
    await this.removeStylingForCurrentSelection();

    const tilesetId = TilesetPlugin.getTilesetIdFromTileset(feature.tileset);
    const elementId = TilesetPlugin.getElementIdFromFeature(feature);

    this._selectedElement =
      tilesetId && elementId ? { tilesetId, elementId } : null;
    this._selectedFeature = feature;
    this._selectedProperties = this.extractProperties(feature);
    this._isFeatureSelected.set(true);

    await this.applyStylingForCurrentSelection();
  }

  async clearSelection() {
    await this.removeStylingForCurrentSelection();
    this._selectedElement = null;
    this._selectedFeature = null;
    this._selectedProperties = null;
    this._isFeatureSelected.set(false);
  }

  getSelectedFeature(): TilesetFeature | null {
    return this._selectedFeature;
  }

  private async handleTileVisible(event: {
    tilesetId: string;
    tileset: Cesium3DTileset;
    tile: Cesium3DTile;
  }) {
    if (!this._selectedElement || this._selectedFeature) return;
    if (event.tilesetId !== this._selectedElement.tilesetId) return;

    const feature = this.findFeatureInTile(
      event.tile,
      this._selectedElement.elementId,
    );
    if (feature) {
      this._selectedFeature = feature;
      this._selectedProperties = this.extractProperties(feature);
      this._isFeatureSelected.set(true);

      await this.removeStylingForCurrentSelection(); // Not sure why but style wont show unless we make sure to remove the rule and then apply again
      await this.applyStylingForCurrentSelection();
    }
  }

  private findFeatureInTile(
    tile: Cesium3DTile,
    elementId: string,
  ): TilesetFeature | null {
    const content: TileContentFeatures | undefined = tile.content;
    if (!content?.featuresLength) return null;

    for (let i = 0; i < content.featuresLength; i++) {
      // Vector content does not default the property table id and holds only
      // the rows some vertex references. Mesh content ignores the table id.
      const feature = content.getFeature(i, 0);
      if (!feature) continue;
      const featureElementId = TilesetPlugin.getElementIdFromFeature(feature);
      if (featureElementId === elementId) {
        return feature;
      }
    }
    return null;
  }

  private extractProperties(feature: TilesetFeature): FeatureProperty[] {
    const propertyIds = feature.getPropertyIds();
    return propertyIds.map((name) => ({
      name,
      value: feature.getProperty(name),
    }));
  }

  private async applyStylingForCurrentSelection() {
    if (!this._selectedElement) return;

    const rule = {
      priority: TilesetStylePriority.ElementSelection,
      elementStyle: {
        tilesetId: this._selectedElement.tilesetId,
        elementId: this._selectedElement.elementId,
        color: "#00FF00",
        opacity: 1,
      },
    };
    this._tilesetStylingPlugin.setRule(rule);
    await this._tilesetStylingPlugin.applyRules();
  }

  private async removeStylingForCurrentSelection() {
    if (!this._selectedElement) return;

    this._tilesetStylingPlugin.removeRule({
      priority: TilesetStylePriority.ElementSelection,
      elementStyle: {
        tilesetId: this._selectedElement.tilesetId,
        elementId: this._selectedElement.elementId,
      },
    });
    await this._tilesetStylingPlugin.applyRules();
  }
}
